import React, { useState, useRef } from "react";
import { Routes, Route, Link, Navigate, useLocation } from "react-router-dom";
import Markdown from "react-markdown";
import { AuthProvider, useAuth } from "./useAuth";
import LoginPage from "./LoginPage";
import HistoryPage from "./HistoryPage";
import "./App.css";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="logs-loading">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function NavBar() {
  const { user, logout } = useAuth();
  const location = useLocation();

  return (
    <nav className="nav-bar">
      <Link to="/" className={`nav-link ${location.pathname === "/" ? "active" : ""}`}>
        Prompt
      </Link>
      <Link to="/history" className={`nav-link ${location.pathname === "/history" ? "active" : ""}`}>
        History
      </Link>
      <div className="nav-spacer" />
      {user ? (
        <div className="nav-user">
          <span className="nav-username">{user.username}</span>
          <button className="nav-logout" onClick={logout}>Logout</button>
        </div>
      ) : (
        <Link to="/login" className={`nav-link ${location.pathname === "/login" ? "active" : ""}`}>
          Login
        </Link>
      )}
    </nav>
  );
}

function PromptPage() {
  const [apiKey, setApiKey] = useState(() => localStorage.getItem("apiKey") || "");
  const [prompt, setPrompt] = useState("");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(true);
  const [generateProposal, setGenerateProposal] = useState(false);
  const [files, setFiles] = useState([]);
  const outputRef = useRef(null);
  const fileInputRef = useRef(null);

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB per file

  const totalFileSize = files.reduce((sum, f) => sum + f.size, 0);

  const formatSize = (bytes) => {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / 1024).toFixed(0)} KB`;
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    const rejected = [];
    const accepted = [];
    for (const f of newFiles) {
      if (f.size > MAX_FILE_SIZE) {
        rejected.push(`${f.name} (${formatSize(f.size)} exceeds 10 MB limit)`);
      } else {
        accepted.push(f);
      }
    }
    if (rejected.length > 0) {
      alert(`Files too large:\n${rejected.join("\n")}`);
    }
    if (accepted.length > 0) {
      setFiles((prev) => [...prev, ...accepted]);
    }
    e.target.value = "";
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prompt.trim() || loading) return;

    setOutput("");
    setLoading(true);

    localStorage.setItem("apiKey", apiKey);

    const formData = new FormData();
    formData.append("apiKey", apiKey);
    formData.append("stream", String(streaming));
    formData.append("generateProposal", String(generateProposal));
    formData.append("prompt", prompt.trim());
    files.forEach((f) => formData.append("files", f));

    try {
      const response = await fetch("/api/prompt", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: "Request failed" }));
        setOutput(`[ERROR] ${err.error}`);
        setLoading(false);
        return;
      }

      if (streaming) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n\n");
          buffer = lines.pop();

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === "output") {
                setOutput((prev) => prev + data.text);
              } else if (data.type === "error") {
                setOutput((prev) => prev + `\n[ERROR] ${data.text}`);
              } else if (data.type === "done") {
                setLoading(false);
              }
            } catch {
              // skip malformed SSE chunks
            }
          }

          if (outputRef.current) {
            outputRef.current.scrollTop = outputRef.current.scrollHeight;
          }
        }
      } else {
        const data = await response.json();
        setOutput(data.output + (data.error ? `\n[ERROR] ${data.error}` : ""));
      }
    } catch (err) {
      setOutput(`Failed to connect to server: ${err.message}`);
    }

    setLoading(false);
    setFiles([]);
  };

  return (
    <>
      <form className="prompt-form" onSubmit={handleSubmit}>
        <input
          className="api-key-input"
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="Enter API key..."
          disabled={loading}
        />
        <textarea
          className="prompt-input"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Enter your prompt here..."
          rows={5}
          disabled={loading}
        />
        <div className="form-actions">
          <input
            type="file"
            multiple
            ref={fileInputRef}
            onChange={handleFileChange}
            className="file-input-hidden"
            disabled={loading}
          />
          <button
            type="button"
            className="attach-btn"
            onClick={() => fileInputRef.current.click()}
            disabled={loading}
          >
            + Attach Files
          </button>
          <label className="stream-toggle">
            <input
              type="checkbox"
              checked={generateProposal}
              onChange={(e) => setGenerateProposal(e.target.checked)}
              disabled={loading}
            />
            Generate Proposal
          </label>
          <label className="stream-toggle">
            <input
              type="checkbox"
              checked={streaming}
              onChange={(e) => setStreaming(e.target.checked)}
              disabled={loading}
            />
            Stream response
          </label>
          <button className="submit-btn" type="submit" disabled={loading || !prompt.trim() || !apiKey.trim()}>
            {loading ? "Running..." : "Submit"}
          </button>
        </div>

        {files.length > 0 && (
          <div className="file-list">
            <span className="file-list-label">
              {files.length} file{files.length > 1 ? "s" : ""} attached ({formatSize(totalFileSize)})
            </span>
            {files.map((f, i) => (
              <div key={i} className="file-chip">
                <span className="file-icon">&#128206;</span>
                <span className="file-name">{f.name}</span>
                <span className="file-size">({formatSize(f.size)})</span>
                <button
                  type="button"
                  className="file-remove"
                  onClick={() => removeFile(i)}
                  disabled={loading}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </form>

      {(output || loading) && (
        <div className="output-section">
          <h2>Output</h2>
          <div className="output-box" ref={outputRef}>
            {output ? <Markdown>{output}</Markdown> : "Waiting for response..."}
          </div>
        </div>
      )}
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <div className="app">
        <header className="header">
          <h1>Claude Code API</h1>
          <p>Enter a prompt to execute via Claude CLI</p>
          <NavBar />
        </header>

        <Routes>
          <Route path="/" element={<PromptPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/history" element={
            <ProtectedRoute>
              <HistoryPage />
            </ProtectedRoute>
          } />
        </Routes>
      </div>
    </AuthProvider>
  );
}

export default App;
